import { forwardRef } from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import { TopCorner, BottomCorner, Flourish } from "./ui/InvoiceCorners";
import signetureImg from "../image/signetureImg";

const fmt = (n) => {
  const num = Number(n) || 0;
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(num);
};

const BASE_PAGE_HEIGHT = 1000;
const PAGE_HEIGHT = 1120;

const FIRST_PAGE_ITEM_LIMIT = 4;
const NEXT_PAGE_ITEM_LIMIT = 4;

const chunkItems = (items = [], chunkSize = 1) => {
  const safeSize = Math.max(1, Number(chunkSize) || 1);
  const chunks = [];

  for (let index = 0; index < items.length; index += safeSize) {
    chunks.push(items.slice(index, index + safeSize));
  }

  return chunks;
};

export const computeTotals = (data = {}) => {
  const items = Array.isArray(data.items) ? data.items : [];
  let subTotal;
  let discount = Number(data.discount || data.discountAmount || 0);
  const providedTotal = Number(data.totalAmount || data.amount || 0);
  const providedTaxAmount = Number(data.taxAmount || 0);
  const cgstRate = Number(data.cgstRate || 0);
  const sgstRate = Number(data.sgstRate || 0);
  const igstRate = Number(data.igstRate || 0);
  // Inter-state supply (customer outside Gujarat) → whole tax is IGST.
  const isInterState = data.isIntraState === false;

  // Check if it's a Plan type
  const isPlanType = items.some(item => item?.type === "Plan") || !data?.isAddon;

  if (items.length > 0) {
    subTotal = items.reduce((sum, item, index) => {
      const shouldInclude = item?.includeInTotal !== false || index === 0;
      if (!shouldInclude) return sum;
      return sum + (Number(item?.amount) || 0);
    }, 0);
  } else {
    subTotal = Number(data.originalPrice || 0);
  }

  // For Plan type, always use originalPrice
  if (isPlanType && data.originalPrice) {
    subTotal = Number(data.originalPrice);
  }

  const netTotal = Math.max(0, subTotal - discount);
  
  let cgst = 0;
  let sgst = 0;
  let igst = 0;
  let totalTax = 0;
  let total = 0;

  // Use the provided total amount from backend if available
  if (providedTotal > 0) {
    total = providedTotal;
    
    // Use provided taxAmount if available (exact value, no rounding)
    if (providedTaxAmount > 0) {
      totalTax = providedTaxAmount;
    } else {
      // If no taxAmount provided, calculate it from total and netTotal
      totalTax = Math.max(0, total - netTotal);
    }
    
    // Split equally between CGST and SGST
    cgst = totalTax / 2;
    sgst = totalTax / 2;
  } else {
    // Fallback: calculate everything normally if no total provided
    // Use the provided taxAmount from backend if available (exact value)
    if (providedTaxAmount > 0) {
      totalTax = providedTaxAmount;
      cgst = totalTax / 2;
      sgst = totalTax / 2;
    } else {
      // Fallback: calculate tax directly from netTotal if no taxAmount provided
      if (isInterState) {
        totalTax = (netTotal * (igstRate || 18)) / 100;
      } else {
        if (cgstRate > 0) {
          cgst = (netTotal * cgstRate) / 100;
        }
        if (sgstRate > 0) {
          sgst = (netTotal * sgstRate) / 100;
        }
        totalTax = cgst + sgst;
      }
    }

    total = netTotal + totalTax;
  }

  if (isInterState) {
    igst = totalTax;
    cgst = 0;
    sgst = 0;
  }

  return { subTotal, netTotal, cgst, sgst, igst, total, taxAmount: totalTax };
};

const formatInvoiceDate = (value) => {
  if (!value) return "";
  const dateValue = new Date(value);
  return Number.isNaN(dateValue.getTime()) ? String(value) : dateValue.toLocaleDateString("en-GB");
};

const InvoicePreview = forwardRef(
  ({ data }, ref) => {
    const { subTotal, netTotal, cgst, sgst, igst, total, taxAmount } = computeTotals(data);
    const items = Array.isArray(data?.items) ? data.items : [];
    const accessItems = Array.isArray(data?.accessItems) ? data.accessItems : [];
    const showSummaryBreakdown = data?.showSummaryBreakdown !== false;
    const showAccessSection = data?.showAccessSection !== false;
    const showAddonValidity = data?.showAddonValidity === true;
    const showThankYou = data?.showThankYou !== false;
    const isAddonInvoice = data?.isAddon === true;
    const firstPageItems = items.slice(0, FIRST_PAGE_ITEM_LIMIT);
    const remainingItems = items.slice(FIRST_PAGE_ITEM_LIMIT);
    const extraItemPages = chunkItems(remainingItems, NEXT_PAGE_ITEM_LIMIT);
    const hasExtraPages = extraItemPages.length > 0;
   

    const renderSummaryBlock = () => {
      if (!showSummaryBreakdown) return null;

      const discountVal = Number(data.discount || data.discountAmount || 0);
      const displayDiscount = discountVal;

      // Calculate item total first so plan + addon invoices can show the gross amount
      const sumItemAmounts = items.reduce((sum, it) => {
        // Offer rows (free bonus months) are informational and never add to the bill.
        if (it?.type === "Offer") return sum;
        let itemAmount = Number(it?.amount) || 0;
        if (it?.type === "Plan" && data?.originalPrice !== undefined) {
          itemAmount = Number(data.originalPrice);
        }
        return sum + itemAmount;
      }, 0);

      const hasPlanAndAddonItems =
        items.some((it) => String(it?.type || "").trim().toLowerCase() === "plan") &&
        items.some((it) => String(it?.type || "").trim().toLowerCase() === "addon");

      const displayOriginalPrice = hasPlanAndAddonItems
        ? sumItemAmounts
        : Number(data.originalPrice || 0);

      const isInterState = data.isIntraState === false;
      const displayCgst = isInterState ? 0 : (data.cgst || cgst);
      const displaySgst = isInterState ? 0 : (data.sgst || sgst);
      const displayIgst = isInterState ? (data.igst || igst) : 0;
      const displayTotal = data.totalAmount || data.amount || total;
      const displaySubTotal = sumItemAmounts - Number(displayDiscount);

      return (
      <div className="mt-4 flex items-start justify-between gap-4">
        <div
          className="w-[340px] rounded-md px-5 py-3 text-[13px]"
          style={{
            border: "1px solid rgba(80,54,145,0.35)",
            backgroundColor: "rgba(247,234,208,0.28)",
          }}
        >
          {!isAddonInvoice && displayOriginalPrice > 0 && (
            <Row label="Original Price" value={`₹ ${Number(displayOriginalPrice).toFixed(2)}`} />
          )}
          {displayDiscount > 0 && (
            <Row label="Discount" value={`- ₹ ${Number(displayDiscount).toFixed(2)}`} />
          )}
          <Row label="Sub Total" value={`₹ ${Number(displaySubTotal).toFixed(2)}`} />
          {displayCgst > 0 && (
            <Row label={`CGST (${data.cgstRate}%)`} value={`₹ ${Number(displayCgst).toFixed(2)}`} />
          )}
          {displaySgst > 0 && (
            <Row label={`SGST (${data.sgstRate}%)`} value={`₹ ${Number(displaySgst).toFixed(2)}`} />
          )}
          {displayIgst > 0 && (
            <Row label={`IGST (${data.igstRate || 18}%)`} value={`₹ ${Number(displayIgst).toFixed(2)}`} />
          )}
          
        </div>

        <div
          className="mt-1 w-[240px] rounded-md px-5 py-3 text-center text-base font-bold tracking-[0.18em]"
          style={{
            background: "linear-gradient(135deg, #2e2386 0%, #503691 60%, #c8a86a 160%)",
            color: "#f7ead0",
          }}
        >
          TOTAL: ₹ {Number(displayTotal).toFixed(2)}
        </div>
      </div>
      );
    };

    const renderAccessSection = () => {
      if (!showAccessSection || !accessItems || accessItems.length === 0) return null;

      // Ensure we display all items - both subscription features and addon items
      const displayItems = accessItems.filter(item => item && item.title);

      if (displayItems.length === 0) return null;

      return (
        <div className="mt-4 rounded-md border px-4 py-3" style={{ borderColor: "rgba(80,54,145,0.22)", backgroundColor: "rgba(80,54,145,0.04)" }}>
          <p className="text-[10px] font-semibold tracking-[0.25em]" style={{ color: "#503691" }}>
            ACCESS / FEATURES
          </p>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {displayItems.map((accessItem, index) => (
              <div
                key={`${accessItem?.title || "access"}-${index}`}
                className="rounded-md border px-3 py-2 text-sm"
                style={{ borderColor: "rgba(80,54,145,0.18)", backgroundColor: "rgba(255,255,255,0.9)" }}
              >
                <div className="font-semibold text-[#141d32]">{accessItem?.title || "Access"}</div>
                {accessItem?.durationInMonth && (
                  <div className="mt-1 text-xs text-[#503691] font-medium">Duration: {accessItem.durationInMonth} month(s)</div>
                )}
                {accessItem?.expiredDate && (
                  <div className="mt-0.5 text-xs text-[#1e1e1e]/70">Expires: {accessItem.expiredDate}</div>
                )}
                {accessItem?.meta && (
                  <div className="mt-0.5 text-xs text-[#1e1e1e]/70">{accessItem.meta}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    };

    const renderAddonDateBlock = () => {
      if (!showAddonValidity) return null;
      const startDate = data?.startDate ? formatInvoiceDate(data.startDate) : "N/A";
      const endDate = data?.endDate ? formatInvoiceDate(data.endDate) : "N/A";
      const duration = data?.itemColumnLabel && data?.items?.[0]?.duration ? data.items[0].duration : "N/A";
      const durationLabel = data?.itemColumnLabel || "Duration";

      return (
        <div className="mt-4 rounded-md border px-4 py-3" style={{ borderColor: "rgba(80,54,145,0.22)", backgroundColor: "rgba(80,54,145,0.04)" }}>
          <p className="text-[10px] font-semibold tracking-[0.25em]" style={{ color: "#503691" }}>
            ADDON VALIDITY
          </p>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "rgba(80,54,145,0.18)", backgroundColor: "rgba(255,255,255,0.9)" }}>
              <div className="text-xs font-semibold uppercase tracking-wider text-[#503691]">Start Date</div>
              <div className="mt-1 font-semibold text-[#141d32]">{startDate}</div>
            </div>
            <div className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "rgba(80,54,145,0.18)", backgroundColor: "rgba(255,255,255,0.9)" }}>
              <div className="text-xs font-semibold uppercase tracking-wider text-[#503691]">End Date</div>
              <div className="mt-1 font-semibold text-[#141d32]">{endDate}</div>
            </div>
            <div className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "rgba(80,54,145,0.18)", backgroundColor: "rgba(255,255,255,0.9)" }}>
              <div className="text-xs font-semibold uppercase tracking-wider text-[#503691]">{durationLabel}</div>
              <div className="mt-1 font-semibold text-[#141d32]">{duration}</div>
            </div>
            {data?.items?.[0]?.durationLabel === "Sub Users" && (
              <div className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "rgba(80,54,145,0.18)", backgroundColor: "rgba(255,255,255,0.9)" }}>
                <div className="text-xs font-semibold uppercase tracking-wider text-[#503691]">Sub Users Limit</div>
                <div className="mt-1 font-semibold text-[#141d32]">{data.items[0].duration}</div>
              </div>
            )}
          </div>
        </div>
      );
    };

    const renderThankYouContent = () => {
      if (!showThankYou) return null;

      return (
      <div className="relative px-12 pt-[180px] pb-8">
        <div className="mt-10 flex items-end justify-between">
          <div>
            <p className="font-script text-5xl" style={{ color: "#503691" }}>
              Thank You!
            </p>
            <p className="mt-4 text-xs tracking-[0.3em] text-[#1e1e1e]/70">
              FOR YOUR BUSINESS
            </p>
          </div>
          <div className="text-center">
            <div className="h-px w-48" style={{ backgroundColor: "#503691" }} />
            <p className="mt-2 text-xs tracking-[0.25em]">AUTHORISED SIGNATORY</p>
            <p className="text-[10px] font-semibold tracking-wider" style={{ color: "#503691" }}>
              BROX VENTURE PVT LTD
            </p>
          </div>
        </div>

        {renderFooter()}
      </div>
      );
    };

    const renderClosingPage = () => {
      if (!showThankYou) return null;

      return (
      <div
        className="print-page relative mx-auto overflow-hidden bg-white"
        style={{
          width: "794px",
          minHeight: `${PAGE_HEIGHT}px`,
          backgroundImage: "radial-gradient(rgba(200,168,106,0.08) 1px, transparent 1px)",
          backgroundSize: "22px 22px",
          breakInside: "avoid",
        }}
      >
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
          aria-hidden="true"
        >
          <span
            className="select-none text-[205px] font-extrabold leading-none tracking-tighter"
            style={{ color: "rgba(80,54,145,0.08)" }}
          >
            BROX
          </span>
        </div>

        <div className="relative px-12 pt-[180px] pb-8">
          <div className="mt-10 flex items-end justify-between">
            <div>
              <p className="font-script text-5xl" style={{ color: "#503691" }}>
                Thank You!
              </p>
              <p className="mt-4 text-xs tracking-[0.3em] text-[#1e1e1e]/70">
                FOR YOUR BUSINESS
              </p>
            </div>
            <div className="text-center">
              <img src={signetureImg} alt="Authorised Signatory" className="mx-auto mb-1 h-14 object-contain" />
              <div className="h-px w-48" style={{ backgroundColor: "#503691" }} />
              <p className="mt-2 text-xs tracking-[0.25em]">AUTHORISED SIGNATORY</p>
              <p className="text-[10px] font-semibold tracking-wider" style={{ color: "#503691" }}>
                BROX VENTURE PVT LTD
              </p>
            </div>
          </div>

          {renderFooter()}
        </div>

        <div className="pointer-events-none absolute inset-0 z-20">
          <BottomCorner />
        </div>
      </div>
      );
    };

    const renderFooter = () => (
      <div className="mt-6 grid grid-cols-2 gap-8 pb-8">
        <div>
          <h3 className="text-base font-bold">CONTACT :</h3>
          <div className="mt-2 space-y-1.5 text-sm">
            <p className="flex items-center gap-3">
              <Phone size={16} /> 8141817353 / 7433881781
            </p>
            <p className="flex items-center gap-3">
              <Mail size={16} /> admin@probroker.in
            </p>
            <p className="flex items-start gap-3">
              <MapPin size={16} className="mt-0.5 shrink-0" />
              <span>
                Add : GF-001, Mauryansh Elanza, Shyamal Cross Rd, Shyamal,
                Ahmedabad, Gujarat 380015
              </span>
            </p>
          </div>
        </div>
        <div>
          <h3 className="text-base font-bold">BANK DETAILS :</h3>
          <div className="mt-2 space-y-1 text-sm">
            <p>Firm Name- BROX Venture PVt LTD</p>
            <p>Current Account No.- 72268767558</p>
            <p>IFSC Code- IDFB0040301</p>
          </div>
        </div>
      </div>
    );

    const renderItemTable = (tableItems = [], itemOffset = 0) => (
      <div className="relative mt-6 overflow-hidden rounded-md">
        <div
          className="grid grid-cols-12 px-5 py-3 text-xs font-semibold tracking-[0.25em]"
          style={{
            background: "linear-gradient(90deg, #2e2386 0%, #503691 100%)",
            color: "#f7ead0",
          }}
        >
          <div className="col-span-2">ITEM</div>
          <div className="col-span-3">DESCRIPTION</div>
          <div className="col-span-3">HSN CODE</div>
          <div className="col-span-2">TYPE</div>
          <div className="col-span-2 text-right">AMOUNT</div>
        </div>
        <div
          className="border border-t-0"
          style={{ borderColor: "rgba(80,54,145,0.35)" }}
        >
          {tableItems.map((it, idx) => {
            // For Plan type items, show originalPrice instead of amount
            let displayAmount = it?.amount;
            if (it?.type === "Plan" && data?.originalPrice !== undefined) {
              displayAmount = data.originalPrice;
            }
            return (
              <div
                key={`${it?.description || "item"}-${itemOffset + idx}`}
                className="grid grid-cols-12 items-center px-5 py-3 text-sm"
                style={{
                  backgroundColor:
                    idx % 2 === 1
                      ? "rgba(247,234,208,0.22)"
                      : "transparent",
                  borderTop:
                    idx === 0
                      ? "none"
                      : "1px dashed rgba(80,54,145,0.18)",
                }}
              >
                <div className="col-span-2 pl-2">
                  <span
                    className="inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold"
                    style={{
                      backgroundColor: "#503691",
                      color: "#f7ead0",
                    }}
                  >
                    {itemOffset + idx + 1}
                  </span>
                </div>
                <div className="col-span-3 font-medium">{it?.description || "N/A"}</div>
                <div className="col-span-3">{it?.hsnCode || "998314"}</div>
                <div className="col-span-2">{it?.type || "Item"}</div>
                <div className="col-span-2 text-right font-semibold">
                  ₹ {Number(displayAmount || 0).toFixed(2)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );

    return (
      <div ref={ref} className="font-inter text-[#1e1e1e]">
        <div
          className="print-page relative mx-auto overflow-hidden bg-white"
          style={{
            width: "794px",
            minHeight: `${PAGE_HEIGHT}px`,
            backgroundImage: "radial-gradient(rgba(200,168,106,0.08) 1px, transparent 1px)",
            backgroundSize: "22px 22px",
            breakInside: "avoid",
          }}
        >
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
            aria-hidden="true"
          >
            <span
              className="select-none text-[205px] font-extrabold leading-none tracking-tighter"
              style={{ color: "rgba(80,54,145,0.08)" }}
            >
              BROX
            </span>
          </div>

          <TopCorner />
          <BottomCorner />

          <div className="relative px-12 pt-[178px] pb-4">
            <div className="flex items-start justify-between gap-5">
              <div>
                <div className="inline-flex items-center gap-3">
                  <span
                    className="h-px w-10"
                    style={{ backgroundColor: "#c8a86a" }}
                  />
                  <span
                    className="text-[10px] font-semibold tracking-[0.4em]"
                    style={{ color: "#503691" }}
                  >
                    TAX INVOICE
                  </span>
                </div>
                <h1 className="mt-1 text-4xl font-light tracking-[0.15em] text-[#141d32]">
                  INVOICE
                </h1>
                <p className="mt-1.5 text-sm">
                  Invoice Number:{" "}
                  <span className="font-semibold">{data.invoiceNumber || "N/A"}</span>
                </p>
                <p className="text-sm">
                  Date: <span className="font-semibold">{data.date || "N/A"}</span>
                </p>
              </div>

              <div className="text-right">
                <h2
                  className="text-2xl font-extrabold tracking-wide"
                  style={{ color: "#3e35a2" }}
                >
                  BROX VENTURE PVT LTD
                </h2>
                <p className="mt-1 text-sm">CIN : U63120GJ2026PTC175617</p>
                <p className="mt-1 text-sm">GSTIN : 24AAOCB5919Q1ZY</p>
              </div>
            </div>

            <Flourish className="mx-auto mt-5 h-5 w-72" />

            <div className="mt-4 grid grid-cols-2 gap-4">
              <InfoCard title="BILL TO">
                <CardLine label="Name" value={data.customerName || "N/A"} />
                <CardLine label="Email" value={data.customerEmail || "N/A"} />
                <CardLine label="GST" value={data.customerGst || data.user?.UserGSTNO || data.user?.userGSTNo || data.UserGSTNO || data.userGSTNo || "—"} />
                <CardLine label="Add" value={data.customerAddress || "N/A"} />
                {data.customerState && data.customerState !== "N/A" && (
                  <CardLine label="State" value={data.customerState} />
                )}
              </InfoCard>
              <InfoCard title="PAYMENT MODE">
                <CardLine label="Mode" value={data.paymentMode || "N/A"} />
                <CardLine label="Agent Name" value={data.agentName || "N/A"} />
                <CardLine label="Transaction ID" value={data.transactionId || "N/A"} />
              </InfoCard>
            </div>

            {renderItemTable(firstPageItems, 0)}

            {renderSummaryBlock()}

            {renderAddonDateBlock()}
            {renderAccessSection()}
          </div>
        </div>

        {extraItemPages.map((pageItems, pageIndex) => (
          <div
            key={`extra-page-${pageIndex}`}
            className="print-page relative mx-auto overflow-hidden bg-white"
            style={{
              width: "794px",
              minHeight: `${PAGE_HEIGHT}px`,
              backgroundImage: "radial-gradient(rgba(200,168,106,0.08) 1px, transparent 1px)",
              backgroundSize: "22px 22px",
              breakInside: "avoid",
            }}
          >
            {(() => {
              const isLastExtraPage = pageIndex === extraItemPages.length - 1;

              return (
                <>
            <div
              className="pointer-events-none absolute inset-0 flex items-center justify-center"
              aria-hidden="true"
            >
              <span
                className="select-none text-[205px] font-extrabold leading-none tracking-tighter"
                style={{ color: "rgba(80,54,145,0.08)" }}
              >
                BROX
              </span>
            </div>

            <TopCorner />
            <BottomCorner />

            <div className="relative px-12 pt-[64px] pb-6">
              <div className="flex items-start justify-between gap-5">
                <div>
                  <p className="text-[10px] font-semibold tracking-[0.35em] text-[#503691]">INVOICE CONTINUED</p>
                  <h2 className="mt-[30px] text-2xl font-semibold tracking-[0.08em] text-[#141d32]">
                    Additional Details - Page {pageIndex + 2}
                  </h2>
                </div>
                <div className="text-right text-sm">
                  <p>Invoice: <span className="font-semibold">{data.invoiceNumber || "N/A"}</span></p>
                  <p>Date: <span className="font-semibold">{data.date || "N/A"}</span></p>
                </div>
              </div>

              {renderItemTable(pageItems, FIRST_PAGE_ITEM_LIMIT + (pageIndex * NEXT_PAGE_ITEM_LIMIT))}

              {isLastExtraPage ? renderClosingPage() : null}
            </div>
                </>
              );
            })()}
          </div>
        ))}

        {!hasExtraPages ? renderClosingPage() : null}
      </div>
    );
  }
);

InvoicePreview.displayName = "InvoicePreview";

const Row = ({ label, value, bold = false }) => (
  <div
    className={`flex justify-between py-1 ${
      bold ? "border-y border-dashed border-[#503691]/35 my-1 font-semibold" : ""
    }`}
  >
    <span>{label} :</span>
    <span>{value}</span>
  </div>
);

const InfoCard = ({ title, children }) => (
  <div
    className="relative rounded-md border bg-white px-5 py-4"
    style={{ borderColor: "rgba(80,54,145,0.35)" }}
  >
    <span
      className="absolute -top-2 left-4 px-2 text-[10px] font-bold tracking-[0.3em]"
      style={{
        backgroundColor: "white",
        color: "#503691",
      }}
    >
      {title}
    </span>
    <div className="mt-1 space-y-1.5 text-sm">{children}</div>
  </div>
);

const CardLine = ({ label, value }) => (
  <div className="flex gap-3">
    <span
      className="w-[112px] shrink-0 text-xs font-semibold uppercase tracking-wider"
      style={{ color: "#503691" }}
    >
      {label}
    </span>
    <span className="flex-1 break-words">{value}</span>
  </div>
);

export default InvoicePreview;
